// Gedeelde rasterlogica (feat/vandaag-raster, oprichter 5 okt 2026: "Vandaag moet net zoals het
// bord aanpasbaar zijn met een raster"). Eén motor voor het bord (12 x 8 vakken, geen scrollen;
// boardWidgets/grid.ts) en voor Vandaag in de ouderapp (12 kolommen, rijen naar beneden;
// widgets/homeGrid.ts). Wat per raster verschilt, staat in een GridSpec: het aantal kolommen en
// rijen en per soort kaart het minimum en maximum. Puur, zonder Zod en zonder DOM: ook de
// kiosk-chunk (iOS 12, ES2018) importeert dit, dus geen nieuwere taalfuncties.
//
// Botsingen (geen overlap): wie een kaart neerzet waar al iets staat, duwt die kaarten omlaag. Past
// dat niet binnen de rijen en raakt de kaart precies één andere, dan ruilen ze van plek (als die
// andere op de oude plek past). Anders gaat de zet niet door: voorspelbaar, nooit iets kwijt.
const clampN = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
const int = (v) => typeof v === 'number' && Number.isInteger(v);
/** Breedte en hoogte binnen de grenzen van de soort en van het raster. */
function sizeIn(spec, kind, w, h) {
    const l = spec.limits(kind);
    return {
        w: clampN(Math.round(w), Math.min(l.minW, spec.cols), Math.min(l.maxW, spec.cols)),
        h: clampN(Math.round(h), Math.min(l.minH, spec.rows), Math.min(l.maxH, spec.rows)),
    };
}
/** Maat binnen de grenzen van de soort en plek binnen het raster. */
export function gridClamp(spec, card) {
    const s = sizeIn(spec, card.kind, card.w, card.h);
    return {
        kind: card.kind,
        x: clampN(Math.round(card.x), 0, spec.cols - s.w),
        y: clampN(Math.round(card.y), 0, spec.rows - s.h),
        w: s.w,
        h: s.h,
    };
}
export function gridOverlap(a, b) {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
export function gridInside(spec, c) {
    return c.x >= 0 && c.y >= 0 && c.x + c.w <= spec.cols && c.y + c.h <= spec.rows;
}
/** Klopt een indeling: elke soort één keer, binnen het raster, binnen de grenzen, geen overlap. */
export function gridValid(spec, cards) {
    for (let i = 0; i < cards.length; i += 1) {
        const a = cards[i];
        const l = spec.limits(a.kind);
        if (!gridInside(spec, a) || a.w < l.minW || a.h < l.minH || a.w > l.maxW || a.h > l.maxH)
            return false;
        for (let j = i + 1; j < cards.length; j += 1) {
            const b = cards[j];
            if (a.kind === b.kind || gridOverlap(a, b))
                return false;
        }
    }
    return true;
}
/** Eerste vrije plek voor w x h (rij voor rij, van de beginkant af); null = geen plek. */
export function gridFreeSpot(spec, cards, w, h) {
    for (let y = 0; y + h <= spec.rows; y += 1) {
        for (let x = 0; x + w <= spec.cols; x += 1) {
            const probe = { kind: '', x, y, w, h };
            if (!cards.some((c) => gridOverlap(c, probe)))
                return { x, y };
        }
    }
    return null;
}
/** Duw wat de kaart raakt omlaag (en wat dat weer raakt). null = past niet binnen de rijen. */
function pushDown(spec, others, card) {
    const out = others.map((c) => ({ ...c }));
    const movers = [card];
    let guard = 0;
    while (movers.length > 0) {
        guard += 1;
        if (guard > 400)
            return null;
        const m = movers.shift();
        for (const o of out) {
            if (o === m || !gridOverlap(o, m))
                continue;
            o.y = m.y + m.h;
            if (o.y + o.h > spec.rows)
                return null;
            movers.push(o);
        }
    }
    return gridValid(spec, out.concat([card])) ? out : null;
}
/**
 * Zet een kaart op een nieuwe plek of in een nieuwe maat (verplaatsen en formaat). Eerst
 * opgeschoond tot binnen de grenzen; daarna duwen, anders ruilen, anders null (de zet gaat niet
 * door). De volgorde van de lijst blijft gelijk.
 */
export function gridPlace(spec, cards, next) {
    const card = gridClamp(spec, next);
    const before = cards.find((c) => c.kind === card.kind);
    const others = cards.filter((c) => c.kind !== card.kind);
    const put = (rest) => {
        const byKind = {};
        for (const c of rest)
            byKind[c.kind] = c;
        byKind[card.kind] = card;
        const order = before ? cards : cards.concat([card]);
        return order.map((c) => byKind[c.kind]);
    };
    if (!others.some((o) => gridOverlap(o, card)))
        return put(others);
    const pushed = pushDown(spec, others, card);
    if (pushed)
        return put(pushed);
    // Ruilen: precies één kaart in de weg, en die past op de oude plek van deze kaart.
    const hits = others.filter((o) => gridOverlap(o, card));
    if (before && hits.length === 1) {
        const hit = hits[0];
        const moved = gridClamp(spec, { ...hit, x: before.x, y: before.y });
        const rest = others.map((o) => (o.kind === hit.kind ? moved : o));
        if (moved.w === hit.w && moved.h === hit.h && gridValid(spec, rest.concat([card]))) {
            return put(rest);
        }
    }
    return null;
}
/** Verplaatsen naar (x, y); null = past daar niet. */
export function gridMove(spec, cards, kind, x, y) {
    const c = cards.find((it) => it.kind === kind);
    return c ? gridPlace(spec, cards, { ...c, x, y }) : null;
}
/** Nieuwe maat w x h (de beginhoek blijft staan); null = past niet. */
export function gridResize(spec, cards, kind, w, h) {
    const c = cards.find((it) => it.kind === kind);
    if (!c)
        return null;
    const l = spec.limits(kind);
    const nw = clampN(w, l.minW, Math.min(l.maxW, spec.cols - c.x));
    const nh = clampN(h, l.minH, Math.min(l.maxH, spec.rows - c.y));
    if (nw === c.w && nh === c.h)
        return cards.slice();
    return gridPlace(spec, cards, { ...c, w: nw, h: nh });
}
/**
 * Kaarten één voor één neerzetten. Met een geldige plek (x, y >= 0) die vrij is: daar. Anders de
 * eerste vrije plek; past de maat nergens, dan de grootste kleinere maat (oppervlak; bij gelijk de
 * hoogste) tot het minimum. Past zelfs dat niet, dan valt de kaart weg.
 */
export function gridPack(spec, desired) {
    const out = [];
    for (const d of desired) {
        if (out.some((c) => c.kind === d.kind))
            continue;
        const l = spec.limits(d.kind);
        const { w, h } = sizeIn(spec, d.kind, d.w, d.h);
        if (int(d.x) && int(d.y) && d.x >= 0 && d.y >= 0) {
            const at = { kind: d.kind, x: d.x, y: d.y, w, h };
            if (gridInside(spec, at) && !out.some((c) => gridOverlap(c, at))) {
                out.push(at);
                continue;
            }
        }
        let best = null;
        for (let tw = w; tw >= Math.min(l.minW, spec.cols); tw -= 1) {
            for (let th = h; th >= Math.min(l.minH, spec.rows); th -= 1) {
                if (best && tw * th < best.w * best.h)
                    break;
                if (best && tw * th === best.w * best.h && th <= best.h)
                    continue;
                const spot = gridFreeSpot(spec, out, tw, th);
                if (spot) {
                    best = { kind: d.kind, x: spot.x, y: spot.y, w: tw, h: th };
                    break;
                }
            }
        }
        if (best)
            out.push(best);
    }
    return out;
}
/** Leesvolgorde: van boven naar beneden, en op één rij van de beginkant af. */
export function gridReadingOrder(cards) {
    return cards.slice().sort((a, b) => a.y - b.y || a.x - b.x);
}
/**
 * Kaarten die wegvallen (een kaart zonder inhoud: geen open ritten, niemand spaart) laten geen gat
 * achter: wat er recht onder stond, schuift omhoog tot het iets raakt. Kaarten die niet onder een
 * weggevallen (of al opgeschoven) kaart staan, blijven waar ze staan: een bewust lege plek blijft.
 */
export function gridLift(cards, gone) {
    const removed = cards.filter((c) => gone(c.kind));
    if (removed.length === 0)
        return cards.slice();
    const out = [];
    const freed = removed.slice();
    for (const c of gridReadingOrder(cards.filter((it) => !gone(it.kind)))) {
        const under = freed.some((r) => r.x < c.x + c.w && c.x < r.x + r.w && r.y + r.h <= c.y);
        const card = { ...c };
        if (under) {
            while (card.y > 0 && !out.some((o) => gridOverlap(o, { ...card, y: card.y - 1 })))
                card.y -= 1;
            if (card.y !== c.y)
                freed.push(c);
        }
        out.push(card);
    }
    // Terug in de volgorde van de lijst.
    return cards.filter((c) => !gone(c.kind)).map((c) => out.find((o) => o.kind === c.kind));
}
/** Alles zo ver mogelijk omhoog (in leesvolgorde), zonder gaten; de volgorde van de lijst blijft. */
export function gridCompact(cards) {
    const out = [];
    for (const c of gridReadingOrder(cards)) {
        const card = { ...c };
        while (card.y > 0 && !out.some((o) => gridOverlap(o, { ...card, y: card.y - 1 })))
            card.y -= 1;
        out.push(card);
    }
    return cards.map((c) => out.find((o) => o.kind === c.kind));
}
/** Pijltjestoetsen: een stap in vakken (in het Arabisch telt de kolom vanaf rechts). */
export function gridArrow(key, rtl) {
    const a = key === 'ArrowLeft'
        ? [-1, 0]
        : key === 'ArrowRight'
            ? [1, 0]
            : key === 'ArrowUp'
                ? [0, -1]
                : key === 'ArrowDown'
                    ? [0, 1]
                    : null;
    return a ? [rtl && a[0] !== 0 ? -a[0] : a[0], a[1]] : null;
}
//# sourceMappingURL=index.js.map